import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Modal } from '../shared/Modal';
import { reportService } from '../../services/reportService';
import { submissionService } from '../../services/submissionService';
import { mapSupabaseError } from '../../utils/supabaseErrors';
import { formatPeriodLabel } from './periodFormat';
import { useAuth } from '../auth/hooks/useAuth';

export function ReportSubmissionForm({ submission, onSaved, onSubmitted, onClose }) {
  const readOnly = submission.status === 'submitted' || submission.status === 'approved';
  const { userRole } = useAuth();
  const userName = userRole?.name ?? '';
  const [fields, setFields] = useState([]);
  const [values, setValues] = useState({});
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [formFields, existing, countriesData] = await Promise.all([
          reportService.getFormFields(submission.report_system_id),
          reportService.getSubmissionReport(submission.id),
          reportService.getCountries(),
        ]);
        if (!active) return;
        setFields(formFields);
        setCountries(countriesData);
        const initial = {};
        existing.forEach((r) => { initial[r.report_field_id] = String(r.value ?? ''); });
        // Pre-fill "Name" fields flagged Me with the submitter's own name — only
        // when the form is editable and the field is currently blank, so a saved
        // draft value is never overwritten and read-only views are untouched.
        if (!readOnly && userName) {
          formFields.forEach((f) => {
            if (f.field_type === 'name' && f.autofill_me) {
              const cur = initial[f.id];
              if (cur === undefined || String(cur).trim() === '') {
                initial[f.id] = userName;
              }
            }
          });
        }
        // Per-country submissions: the up-front pick is the source of truth. Lock
        // every Country dropdown to it (overriding any stored draft value) so the
        // saved content can never drift from the submission's identity.
        if (!readOnly && submission.country_id) {
          const cname = (countriesData.find((c) => c.id === submission.country_id) || {}).name;
          if (cname) {
            formFields.forEach((f) => { if (f.field_type === 'dropdown') initial[f.id] = cname; });
          }
        }
        setValues(initial);
      } catch (e) {
        if (active) setError(mapSupabaseError(e));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [submission.id, submission.report_system_id, submission.country_id, readOnly, userName]);

  const isBlank = (id) => {
    const v = values[id];
    return v === undefined || v === null || String(v).trim() === '';
  };

  // Items for save: every non-blank value becomes a row (blank ones are skipped
  // in the service, but we trim here too so a whitespace-only entry isn't saved).
  const buildItems = () =>
    fields
      .filter((f) => !isBlank(f.id))
      .map((f) => ({ report_field_id: f.id, value: values[f.id] }));

  const persist = async () => {
    await reportService.saveSubmissionReport(submission.id, submission.user_id, buildItems());
  };

  const handleSave = async () => {
    try { setBusy(true); setError(''); await persist(); toast.success('Draft saved'); onSaved(); }
    catch (e) { setError(mapSupabaseError(e)); } finally { setBusy(false); }
  };

  const handleSubmit = async () => {
    const missing = fields.find((f) => f.is_required && isBlank(f.id));
    if (missing) { setError(`"${missing.label}" is required.`); return; }
    try { setBusy(true); setError(''); await persist(); await submissionService.submit(submission.id); toast.success('Submitted for review'); onSubmitted(); }
    catch (e) { setError(mapSupabaseError(e)); } finally { setBusy(false); }
  };

  const setValue = (id, v) => setValues((prev) => ({ ...prev, [id]: v }));

  const countryName = submission.country_id
    ? (countries.find((c) => c.id === submission.country_id)?.name || '')
    : '';

  return (
    <Modal onClose={onClose} role="dialog" aria-modal="true" aria-label="Report submission">
      <h3>Report submission</h3>
      <p className="form-label">{formatPeriodLabel(submission.period, submission.period_start, submission.period_end)}{countryName ? ` · ${countryName}` : ''}</p>
      {submission.status === 'rejected' && submission.review_comment && (
        <div className="error-message" role="alert"><strong>Reviewer note:</strong> {submission.review_comment}</div>
      )}
      {error && <div className="error-message" role="alert">{error}</div>}

      {loading ? <p className="loading-msg">Loading…</p> : (
        <>
          {fields.map((f) => {
            const fieldId = `report-field-${f.id}`;
            const val = values[f.id] ?? '';
            const desc = typeof f.description === 'string' ? f.description.trim() : '';
            const descId = !readOnly && desc !== '' ? `${fieldId}-desc` : undefined;
            return (
              <div className="form-group" key={f.id}>
                <label htmlFor={fieldId}>
                  <span>{f.label}</span>
                  {f.is_required && <span aria-hidden="true"> *</span>}
                </label>
                {descId && (
                  <p className="report-field-description" id={descId}>{desc}</p>
                )}
                {readOnly ? (
                  <p className="report-value">{val === '' ? '—' : val}</p>
                ) : f.field_type === 'textarea' ? (
                  <textarea id={fieldId} rows={4} maxLength={5000} value={val} required={f.is_required}
                    onChange={(e) => setValue(f.id, e.target.value)} aria-label={f.label} aria-describedby={descId} />
                ) : f.field_type === 'dropdown' ? (
                  <select id={fieldId} value={val} required={f.is_required} disabled={!!submission.country_id}
                    onChange={(e) => setValue(f.id, e.target.value)} aria-label={f.label} aria-describedby={descId}>
                    <option value="">— Select —</option>
                    {countries.map((c) => (
                      <option key={c.id} value={c.name}>{c.name}</option>
                    ))}
                  </select>
                ) : (
                  <input id={fieldId} type="text" maxLength={1000} value={val} required={f.is_required}
                    onChange={(e) => setValue(f.id, e.target.value)} aria-label={f.label} aria-describedby={descId} />
                )}
              </div>
            );
          })}

          <div className="modal-buttons">
            {!readOnly && (
              <>
                <button type="button" className="btn-secondary" onClick={handleSave} disabled={busy}>Save draft</button>
                <button type="button" className="btn-edit" onClick={handleSubmit} disabled={busy}>Submit</button>
              </>
            )}
            <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>Close</button>
          </div>
        </>
      )}
    </Modal>
  );
}
